import { useEffect, useRef, useState } from "react";
import { api, API_BASE_URL } from "../lib/api";
import { useToast } from "../lib/toast";
import { UrlImporter } from "./UrlImporter";
import { ImageLightbox } from "./ImageLightbox";

const DEFAULT_IMAGE_MODEL = "google/gemini-2.5-flash-image";

interface Props {
  images: string[];
  onChange: (next: string[]) => void;
  /** Prefills the generation prompt (e.g. character/setting description). */
  defaultPrompt?: string;
  /** Character editor: show auto turnaround generator. */
  enableAutoViews?: boolean;
}

export function ReferenceImagesField({
  images,
  onChange,
  defaultPrompt = "",
  enableAutoViews = false,
}: Props) {
  const toast = useToast();
  const zoneRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const [tab, setTab] = useState<"upload" | "generate">("upload");
  const [uploading, setUploading] = useState(false);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [promptTouched, setPromptTouched] = useState(false);
  const [model, setModel] = useState(DEFAULT_IMAGE_MODEL);
  const [generating, setGenerating] = useState(false);
  const [autoStatus, setAutoStatus] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  /** URLs checked to feed into the next image-generation call. */
  const [genRefUrls, setGenRefUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!promptTouched) setPrompt(defaultPrompt);
  }, [defaultPrompt, promptTouched]);

  // Drop selections that no longer exist in the image list.
  useEffect(() => {
    setGenRefUrls((cur) => cur.filter((u) => images.includes(u)));
  }, [images]);

  function setImages(next: string[]) {
    imagesRef.current = next;
    onChange(next);
  }

  function toggleGenRef(url: string) {
    setGenRefUrls((cur) =>
      cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url]
    );
  }

  async function uploadFiles(files: File[]) {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API_BASE_URL}/media/upload`, {
          method: "POST",
          headers: api.projectHeaders(),
          body: fd,
        });
        if (!res.ok) {
          toast.show("Upload failed", "error");
          continue;
        }
        const data = await res.json();
        setImages([...imagesRef.current, data.url]);
      }
    } finally {
      setUploading(false);
    }
  }

  async function onImportUrl(url: string) {
    try {
      const res = await api.post<{ url: string }>("/media/import-url", { url });
      setImages([...imagesRef.current, res.url]);
    } catch (err: any) {
      toast.show(err?.message ?? "Import failed", "error");
    }
  }

  async function generateOne(
    genPrompt: string,
    referenceImageUrls: string[]
  ): Promise<string> {
    const res = await api.post<{ url: string; model: string }>("/media/generate-image", {
      prompt: genPrompt,
      model: model.trim() || DEFAULT_IMAGE_MODEL,
      referenceImageUrls,
    });
    return res.url;
  }

  async function generate() {
    if (!prompt.trim()) {
      toast.show("Enter a prompt", "error");
      return;
    }
    setGenerating(true);
    try {
      const url = await generateOne(prompt.trim(), genRefUrls);
      setImages([...imagesRef.current, url]);
      toast.show(
        genRefUrls.length
          ? `Image added (used ${genRefUrls.length} ref${genRefUrls.length === 1 ? "" : "s"})`
          : "Image added to references",
        "success"
      );
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Generate failed", "error");
    } finally {
      setGenerating(false);
    }
  }

  /**
   * Server-side turnaround: front → back → left → right → final back.
   */
  async function autoGenerateViews() {
    const desc = (prompt.trim() || defaultPrompt.trim());
    if (!desc) {
      toast.show("Add a character description first", "error");
      return;
    }

    setGenerating(true);
    setTab("generate");
    setAutoStatus("Generating turnaround views…");

    try {
      const res = await api.post<{ urls: string[]; model: string }>("/media/generate-turnaround", {
        subjectDescription: desc,
        model: model.trim() || DEFAULT_IMAGE_MODEL,
      });
      setImages([...imagesRef.current, ...res.urls]);
      setGenRefUrls(res.urls);
      setAutoStatus(null);
      toast.show("Auto-generated front, back, left, right, and final back views", "success");
    } catch (err: any) {
      setAutoStatus(null);
      toast.show(err?.details?.message ?? err?.message ?? "Auto-generate failed", "error");
    } finally {
      setGenerating(false);
    }
  }

  function filesFromClipboard(e: React.ClipboardEvent): File[] {
    const items = e.clipboardData?.items;
    if (!items) return [];
    const out: File[] = [];
    for (const item of Array.from(items)) {
      if (!item.type.startsWith("image/")) continue;
      const file = item.getAsFile();
      if (file) out.push(file);
    }
    return out;
  }

  function onPaste(e: React.ClipboardEvent) {
    const files = filesFromClipboard(e);
    if (!files.length) return;
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    uploadFiles(files.slice(0, 1));
  }

  const busy = generating || uploading;

  return (
    <div className="field">
      <label>Reference images</label>
      <div className="tiny muted" style={{ marginBottom: 6 }}>
        Best results: 1–3 clean, well-lit portraits (front / 3/4 / profile), same wardrobe, no heavy filters. First image is the primary likeness.
      </div>
      <div className="ref-tabs" aria-label="Reference image source">
        <button
          type="button"
          className={`ref-tab ${tab === "upload" ? "active" : ""}`}
          onClick={() => setTab("upload")}
          disabled={busy}
        >
          Image Upload
        </button>
        <button
          type="button"
          className={`ref-tab ${tab === "generate" ? "active" : ""}`}
          onClick={() => setTab("generate")}
          disabled={busy}
        >
          Image Generation
        </button>
      </div>
      <div className="ref-images-main">
        {tab === "upload" && (
          <div>
            <UrlImporter onImport={onImportUrl} />
            <div className="row" style={{ marginTop: 8, alignItems: "center", gap: 8 }}>
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={busy}
                onChange={(e) => {
                  const list = e.target.files ? Array.from(e.target.files) : [];
                  uploadFiles(list);
                  e.target.value = "";
                }}
              />
            </div>
            <div
              ref={zoneRef}
              className="paste-zone"
              tabIndex={0}
              onPaste={onPaste}
              onClick={() => zoneRef.current?.focus()}
            >
              {uploading ? "Uploading…" : "Click here and paste an image (Ctrl+V)"}
            </div>
          </div>
        )}

        {tab === "generate" && (
          <div>
            <div className="field" style={{ marginBottom: 8 }}>
              <label>Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => {
                  setPromptTouched(true);
                  setPrompt(e.target.value);
                }}
                rows={4}
                placeholder="Describe the image to generate…"
                disabled={busy}
              />
            </div>
            <div className="field" style={{ marginBottom: 8 }}>
              <label>Model</label>
              <input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder={DEFAULT_IMAGE_MODEL}
                disabled={busy}
              />
              <div className="tiny muted" style={{ marginTop: 4 }}>
                Cheap default: {DEFAULT_IMAGE_MODEL}
              </div>
            </div>
            {images.length > 0 && (
              <div className="tiny muted" style={{ marginBottom: 8 }}>
                Check images below to use as identity refs for this generation
                {genRefUrls.length > 0
                  ? ` (${genRefUrls.length} selected).`
                  : "."}
              </div>
            )}
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <button
                className="primary"
                disabled={busy || !prompt.trim()}
                onClick={generate}
              >
                {generating && !autoStatus
                  ? "Generating…"
                  : genRefUrls.length
                    ? `Generate with ${genRefUrls.length} ref${genRefUrls.length === 1 ? "" : "s"}`
                    : "Generate"}
              </button>
              {enableAutoViews && (
                <button
                  type="button"
                  disabled={busy || !(prompt.trim() || defaultPrompt.trim())}
                  onClick={autoGenerateViews}
                  title="Front → back → left → right, then a final back using all views. White background."
                >
                  {autoStatus ? autoStatus : "Auto-generate views"}
                </button>
              )}
            </div>
            {enableAutoViews && !autoStatus && (
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Auto-generate views: front, then back, left, right (each using prior images as refs), then a final back with all refs — white studio background, using the description above.
              </div>
            )}
          </div>
        )}

        {images.length > 0 && (
          <div className="image-grid" style={{ marginTop: 12 }}>
            {images.map((u, idx) => {
              const checked = genRefUrls.includes(u);
              return (
                <div key={u + idx} className="ref-thumb" style={{ position: "relative" }}>
                  <img
                    src={api.mediaUrl(u)}
                    alt=""
                    onClick={() => setLightboxIndex(idx)}
                  />
                  <label
                    className={`ref-gen-check ${checked ? "checked" : ""}`}
                    title="Use as reference for next generation"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={busy}
                      onChange={() => toggleGenRef(u)}
                    />
                  </label>
                  <button
                    className="danger"
                    style={{ position: "absolute", top: 4, right: 4, padding: "2px 6px", fontSize: 11 }}
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      setImages(images.filter((_, i) => i !== idx));
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {lightboxIndex != null && (
        <ImageLightbox
          urls={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
}
